// i18n-ignore-file — 繁中原話，只在 zh-Hant 首頁顯示（D-275, D-277）。
// Homepage block 老師回饋: verbatim excerpts from teachers' own feedback, surname only.
// Sources (ticket ids) live in docs/marketing/teacher-voices.md, never on the page.
// Rules: cut only, never reword; "…" marks a cut inside a sentence; no child names, schools, classes or emails.
import { useEffect, useRef, useState } from "react";
import "./home-teacher-voices.css";

type Voice = { text: string; by: string };

// The first one opens on the stage; every quote is also always readable in the list (no hover needed).
const VOICES: Voice[] = [
  { text: "孩子們不需要被責備，效果很正向", by: "吳老師" },
  { text: "太喜歡這個網頁了，完全引發了孩子們的學習動機。", by: "Chen 老師" },
  { text: "謝謝小森的你們提供老師們這麼好的平台，我目前在使用上覺得對班經非常有幫助，學生也很喜歡新出的樂園畫面", by: "謝老師" },
  { text: "感謝您開發這個系統，很實用", by: "蔡老師" },
  { text: "好有趣的班經互動", by: "昌老師" },
  { text: "新功能很讚!", by: "林老師" },
];

const ROTATE_MS = 8000;
const LONG_QUOTE = 26;

// Short quotes keep each phrase whole (no break inside 責備 or 效果); long ones wrap freely.
function StageText({ text }: { text: string }) {
  if (text.length > LONG_QUOTE) return <>{text}</>;
  return <>{text.split(/(?<=[，。])/).map((phrase) => <span key={phrase}>{phrase}</span>)}</>;
}
// Card text wraps freely in narrow cards; 「 stays with the first character and 」 with the last two,
// so a bracket or a single character never sits alone on a line.
function CardText({ text }: { text: string }) {
  const chars = Array.from(text);
  return <><span className="home-voice-glue">「{chars[0]}</span>{chars.slice(1, -2).join("")}<span className="home-voice-glue">{chars.slice(-2).join("")}」</span></>;
}

export function HomeTeacherVoices() {
  const [active, setActive] = useState(0);
  // Slow rotation only until the teacher picks one; paused while hovered, focused, off-screen or hidden.
  const [chosen, setChosen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pageHidden, setPageHidden] = useState(false);
  const [calm, setCalm] = useState(true);
  const [announced, setAnnounced] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const media = typeof matchMedia === "function" ? matchMedia("(prefers-reduced-motion: reduce)") : null;
    const sync = () => setCalm(Boolean(media?.matches));
    sync();
    media?.addEventListener("change", sync);
    const syncPage = () => setPageHidden(document.hidden);
    syncPage();
    document.addEventListener("visibilitychange", syncPage);
    const node = sectionRef.current;
    const observer = node && typeof IntersectionObserver === "function"
      ? new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.35 })
      : null;
    if (node) observer?.observe(node);
    return () => { media?.removeEventListener("change", sync); document.removeEventListener("visibilitychange", syncPage); observer?.disconnect(); };
  }, []);

  const playing = !chosen && !hovered && !focused && visible && !pageHidden && !calm;
  // A fresh full interval after every pause or change, in step with the progress line.
  useEffect(() => {
    if (!playing) return;
    const timer = setTimeout(() => setActive((index) => (index + 1) % VOICES.length), ROTATE_MS);
    return () => clearTimeout(timer);
  }, [playing, active]);

  const pick = (index: number) => { setChosen(true); setAnnounced(true); setActive(index); };
  // The stop button disappears once used, so keyboard focus moves to the card now on the stage.
  const stop = () => { setChosen(true); sectionRef.current?.querySelectorAll<HTMLButtonElement>(".home-voice-card")[active]?.focus(); };

  return <section
    ref={sectionRef}
    className={`home-voices${playing ? " is-playing" : ""}`}
    id="teacher-voices"
    data-home-module="M4-VOICES"
    aria-labelledby="home-voices-title"
    onPointerEnter={(event) => { if (event.pointerType === "mouse") setHovered(true); }}
    onPointerLeave={(event) => { if (event.pointerType === "mouse") setHovered(false); }}
    onFocus={() => setFocused(true)}
    onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false); }}
  >
    <div className="home-voices-head">
      <p className="eyebrow">老師回饋</p>
      <h2 id="home-voices-title"><span>正在使用小森課堂的</span><span>老師怎麼說</span></h2>
      <p>摘錄自老師寄來的回饋原文，僅保留姓氏。</p>
    </div>
    <div className="home-voices-grid">
      {/* Every quote sits in the same cell, so the stage keeps the height of the longest one and never jumps. */}
      <div className="home-voices-stage">
        <span className="home-voices-mark" aria-hidden="true">“</span>
        <div className="home-voices-slides">
          {VOICES.map((voice, index) => <figure
            key={voice.by}
            className={`home-voices-slide${index === active ? " is-active" : ""}${voice.text.length > LONG_QUOTE ? " is-long" : ""}`}
            aria-hidden={index !== active}
          >
            <blockquote><p><StageText text={voice.text} /></p></blockquote>
            <figcaption>{voice.by}</figcaption>
          </figure>)}
        </div>
        {!chosen && !calm && <div className="home-voices-auto">
          <div className="home-voices-progress" aria-hidden="true"><span key={`${active}-${playing}`} /></div>
          <button type="button" className="home-voices-stop" onClick={stop}>停止輪播</button>
        </div>}
      </div>
      {/* Only a teacher's own pick is announced; the slow rotation stays silent. */}
      <p className="home-voices-status" aria-live="polite">{announced ? `${VOICES[active].by}的回饋` : ""}</p>
      <ul className="home-voices-list" aria-label="全部回饋">
        {VOICES.map((voice, index) => <li key={voice.by}>
          <button type="button" className="home-voice-card" aria-pressed={index === active} onClick={() => pick(index)}>
            <span className="home-voice-quote"><CardText text={voice.text} /></span>
            <span className="home-voice-by">{voice.by}</span>
          </button>
        </li>)}
      </ul>
    </div>
  </section>;
}
